import { useMemo, useState } from 'react';
import type { CeoDebtCalendarMonth } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { formatMillions } from '../ceoFormat';
import {
  CalendarGrid,
  HeaderRow,
  MonthBox,
  MonthBoxHeader,
  MonthBoxTitle,
  MonthDetailRow,
  MonthFooterRow,
  MonthPaidText,
  MonthTotal,
  MonthTotalLabel,
  RatesSubtext,
  Section,
  Title,
} from './styles';

interface DebtsCalendarGridProps {
  calendarByMonth: CeoDebtCalendarMonth[];
}

/** По умолчанию — два прошедших месяца, текущий и год вперёд. */
const MONTHS_BEFORE = 2;
const MONTHS_AFTER = 12;

/** Ключ месяца «YYYY-MM» со сдвигом от текущего. */
function monthKey(offset: number): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function DebtsCalendarGrid({ calendarByMonth }: DebtsCalendarGridProps) {
  const [showAll, setShowAll] = useState(false);
  const near = useMemo(() => {
    const from = monthKey(-MONTHS_BEFORE);
    const to = monthKey(MONTHS_AFTER);
    return calendarByMonth.filter((m) => m.month_key >= from && m.month_key <= to);
  }, [calendarByMonth]);
  const months = showAll ? calendarByMonth : near;
  return (
    <Section>
      <HeaderRow>
        <Title>Календарь погашений по месяцам (основной долг + проценты + лизинг)</Title>
        {near.length < calendarByMonth.length && (
          <Button $size="sm" $variant="ghost" type="button" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Ближайшие месяцы' : `Все месяцы (${calendarByMonth.length})`}
          </Button>
        )}
      </HeaderRow>
      {months.length === 0 && (
        <RatesSubtext>В ближайшие месяцы платежей по графикам нет.</RatesSubtext>
      )}
      {/* Одна база с «К оплате» и KPI — остаток: частично оплаченный платёж — неоплаченной частью. */}
      <CalendarGrid>
        {months.map((m) => (
          <MonthBox key={m.month_key}>
            <MonthBoxHeader>
              <MonthBoxTitle>{m.month_label}</MonthBoxTitle>
              <Badge $tone={m.unpaid_amount <= 0.01 ? 'success' : 'info'}>
                {m.items_count} плат.
              </Badge>
            </MonthBoxHeader>
            <MonthTotalLabel>Осталось оплатить</MonthTotalLabel>
            <MonthTotal>{formatMillions(m.unpaid_amount)}</MonthTotal>
            <MonthDetailRow>
              <span>Кредиты (ОД + %):</span>
              <strong>{formatMillions(m.credit_payments_total)}</strong>
            </MonthDetailRow>
            <MonthDetailRow>
              <span>Лизинговые платежи:</span>
              <strong>{formatMillions(m.leasing_payments_total)}</strong>
            </MonthDetailRow>
            {m.guarantee_fees_total > 0 && (
              <MonthDetailRow>
                <span>Комиссии по БГ:</span>
                <strong>{formatMillions(m.guarantee_fees_total)}</strong>
              </MonthDetailRow>
            )}
            <MonthFooterRow>
              <span>По графику: {formatMillions(m.total_payment)}</span>
              <MonthPaidText>Оплачено: {formatMillions(m.paid_amount)}</MonthPaidText>
            </MonthFooterRow>
          </MonthBox>
        ))}
      </CalendarGrid>
    </Section>
  );
}
